import i18next from 'i18next';

import {__isEmpty, gistApi, requestHelper, responseHelper, sourceHelper} from '../_helpers'
import {notificationConstants, sourceConstants} from "../_constants";

/**
 * This service adds the following functionality
 */
export const sourceService = {
    getSources,
    setCurrentStatus,
    orderSource,
    validateSource,
    editSource,
    getSignedUploadUrl,
    upload,
    removeUploadResource,
};

const t = i18next.t;

/**
 * This function fetches the source of the user
 * @returns {Promise<{username: *, token: *} | never>}
 */
function getSources() {
    let myHeader = requestHelper.authHeader();
    let link = '/api/sources';

    return gistApi.get(link, {headers: myHeader})
        .then(response => {
            return responseHelper.handleResponse(response)
        })
        .then(sources => {
            return sourceHelper.parseSources(sources)
        })
        .catch(error => {
            let res = {
                "status": 500,
                "errorMsg": t("Sorry, we're having technical issues. Please try again.")
            };
            if (error && error.response) {
                res.status = error.response.status;
                switch (error.response.status) {
                    case 401:
                        res.errorMsg = t("Sorry, you are unauthorized to get the list.");
                        break;

                    case 402:
                        res = prepareResponseFor402Error(error.response.status, error.response.data)
                        break;

                    default:
                        res.errorMsg = t("Sorry, we're having technical issues. Please try again.");
                        break;
                }
            }
            return Promise.reject( res );
        })
}

/**
 * This function sets the status of the source with uuid as provided
 * @param uuid the uuid of the source
 * @param status the status to be changed to
 * @returns {Promise<never | never>}
 */
function setCurrentStatus(uuid, status) {
    let myHeader = requestHelper.authHeader();
    let link = '/api/sources/' + uuid;
    const data = { "currentStatus": status };

    return gistApi.patch(link, data, {headers: myHeader})
        .then(response => {
            return responseHelper.handleResponse(response)
        })
        .then(sources => {
            return sourceHelper.parseSources(sources)
        })
        .catch(error => {
            let res = {
                "status": 500,
                "errorMsg": t("Sorry, we're having technical issues. Please try again.")
            };
            if (error && error.response) {
                res.status = error.response.status;
                switch (error.response.status) {
                    case 401:
                        switch(status){
                            case sourceConstants.SOURCE_DEACTIVATE:
                                res.errorMsg = t("Sorry, you are not authorized to un-publish the media.");
                                break;

                            case sourceConstants.SOURCE_ACTIVATE:
                                res.errorMsg = t("Sorry, you are not authorized to publish the media.");
                                break;

                            default:
                                res.errorMsg = "";
                                break;
                        }

                        break;

                    case 402:
                        res = prepareResponseFor402Error(error.response.status, error.response.data)
                        break;

                    default:
                        res.errorMsg = t("Sorry, we're having technical issues. Please try again.");
                        break;
                }
            }
            return Promise.reject( res );
        })
}

/**
 * This function orders the given source
 * @param source
 */
function orderSource(source) {
    let myHeader = requestHelper.authHeader();
    let link = '/api/sources';

    return gistApi.post(link, source, {headers: myHeader})
        .then(response => {
            return responseHelper.handleResponse(response)
        })
        .then(sources => {
            return sources
        })
        .catch(error => {
            let res = {
                "status": 500,
                "errorMsg": t("Sorry, we're having technical issues. Please try again.")
            };
            if (error && error.response) {
                res.status = error.response.status;
                switch (error.response.status) {
                    case 401:
                        res.errorMsg = t("Sorry, you are unauthorized to order. Please re-signin.");
                        break;

                    case 402:
                        res = prepareResponseFor402Error(error.response.status, error.response.data)
                        break;

                    case 507:
                        res.errorMsg = t("Sorry, the order cannot be fulfilled because you do not have enough storage available to host the media. Please consider upgrading your account plan.");
                        break;

                    case 422:
                        res.errorMsg = t("Sorry, the media was expected to be hosted at Gistify, but it could not be retrieved. Please start the order process again.");
                        break;

                    default:
                        res.errorMsg = t("Sorry, we're having technical issues. Please try again.");
                        break;
                }
            }
            return Promise.reject( res );
        })
}

/**
 * This function validates the given source ,i.e., a url
 * @param source
 */
function validateSource(source){
    let myHeader = requestHelper.authHeader();
    let link = '/api/sources/validation';
    
    try{
        new URL(source.probeUri);
    } catch(e) {
        // if invalid url
        return Promise.reject( {
            "status": 400,
            "errorMsg": t("We’re having a hard time processing the media at that URL. Is there a typo? If this problem persists, please contact support.")
        });
    }

    return gistApi.post(link, source, {headers: myHeader})
        .then(response => {
            return responseHelper.handleResponse(response)
        })
        .then(sources => {
            return sources
        })
        .catch(error => {
            let res = {
                "status": 500,
                "errorMsg": t("Sorry, we're having technical issues. Please try again.")
            };
            if (error && error.response) {
                res.status = error.response.status;
                switch (error.response.status) {
                    case 400:
                        switch(error.response.data.validationDetails){
                            case sourceConstants.SOURCE_VALIDATE_FAIL.GOOGLE_DRIVE_QUOTA_EXCEEDED:
                                res.errorMsg = t("Sorry, Google Drive says that too many users have viewed or downloaded your file recently, so we're unable to create a Gist. Try again in 24 hours.");
                                break;

                            case sourceConstants.SOURCE_VALIDATE_FAIL.GOOGLE_DRIVE_VIRUS_SCAN_LARGE:
                                res.errorMsg = t("Sorry, Google Drive says your video is too big for virus scanning, so we're unable to create a Gist right now.");
                                break;

                            case sourceConstants.SOURCE_VALIDATE_FAIL.ZOOM_ACCESS_PROTECTED:
                                const link = {
                                    url : "https://gistify.zendesk.com/hc/en-us/articles/360046246514",
                                    text: t("Help with Zoom recording settings"),
                                };
                                res.errorMsg = t("To create a Gist, your Zoom password and security settings need changes.");
                                res.errorMsg += `<br /> <a href=${link.url}>${link.text}</a>`;
                                break;

                            case sourceConstants.SOURCE_VALIDATE_FAIL.UNKNOWN:
                            case null:
                            case undefined:
                            default:
                                res.errorMsg = t("Sorry, we’re only able to create Gists for direct links to videos or audio like MP4, MOV, or MP3’s; we’re not able to do videos on YouTube, Vimeo, TED, etc. yet. We'd love to hear any feedback.");
                                break;
                        }
                        break;

                    case 402:
                        const data = error.response.data;
                        const failureDetails = (__isEmpty(data.validationDetails)) ? data : JSON.parse(data.validationDetails);
                        res = prepareResponseFor402Error(error.response.status, failureDetails);
                        break;

                    case 409:
                        res.errorMsg = t("This recording already has a Gist.");

                        if(error.response.data && error.response.data.validationDetails) {  // build url and add link to error message
                            const details = error.response.data.validationDetails;          // list of conflicting ids
                            const itemArray = details.replace(/[\[\]']+/g,'').split(",");   // arrays of associated uuids
                            const links = sourceHelper.prepareSourceAnchorLinks(itemArray); // get formatted anchor links
                            links.forEach( link => res.errorMsg += link );                  // add to error message
                        }
                        break;

                    case 415:
                        res.errorMsg = t("Sorry, we’re only able to create Gists for direct links to videos or audio like MP4, MOV, or MP3’s; we’re not able to do videos on YouTube, Vimeo, TED, etc. yet. We'd love to hear any feedback.");
                        break;

                    case 507:
                        res.errorMsg = t("Sorry, the order cannot be fulfilled because you do not have enough storage available to host the media. Please consider upgrading your account plan.");
                        break;

                    default:
                        res.errorMsg = t("Sorry, we're having technical issues. Please try again.");
                        break;
                }
            }
            return Promise.reject( res );
        })
}

/**
 * This function updated the source (with uuid) with given values for the property
 * @param uuid the uuid of the source
 * @param param the property of the source to be updated
 * @param value the new value for the property
 * @returns {Promise<{duration: *, viewed, imageUrl, name: *, uploaded: string, id: *, url: string, status: string}>}
 */
function editSource(uuid, param, value){
    let myHeader = requestHelper.authHeader();
    let link = '/api/sources/' + uuid;

    let dataToSend = sourceHelper.prepareParamsForApi(param, value);

    return gistApi.patch(link, dataToSend, {headers: myHeader})
        .then(response => {
            return responseHelper.handleResponse(response)
        })
        .then(sources => {
            // parse the updated source into format expected by the client
            return sourceHelper.parseSources(sources)
        })
        .catch(error => {
            return Promise.reject( responseHelper.handleError(error.response) );
        })
}

/**
 * Returns signed-upload-url for uploading
 *
 * @returns {Promise<never>}
 */
function getSignedUploadUrl(params){
    let myHeader = requestHelper.authHeader();
    let link = '/api/sources/upload';

    return gistApi.post(link, params, {headers: myHeader})
        .then(response => {
            return responseHelper.handleResponse(response)
        })
        .then(response => {
            return response;
        })
        .catch(error => {
            let res = {
                "status": 500,
                "errorMsg": t("Sorry, we're having technical issues. Please try again.")
            };
            if (error && error.response) {
                res.status = error.response.status;
                switch (error.response.status) {
                    case 400:
                        res.errorMsg = t("Sorry, we're having technical issues. Please try again.");
                        break;

                    case 402:
                        res = prepareResponseFor402Error(error.response.status, error.response.data)
                        break;

                    case 415:
                        res.errorMsg = t("Sorry, we’re only able to create Gist for sources that can be played on the browser. Please try with a different file.");
                        break;

                    case 507:
                        res.errorMsg = t("Sorry, the order cannot be fulfilled because you do not have enough storage available to host the media. Please consider upgrading your account plan.");
                        break;

                    default:
                        res.errorMsg = t("Sorry, we're having technical issues. Please try again.");
                        break;
                }
            }
            return Promise.reject( res );
        })
}

/**
 * Uploads data based on the upload-packet
 *
 * @param uploadPacket upload-packet containing signed-url, request-headers and content to upload
 * @returns {Promise<never>}
 */
function upload(uploadPacket){
    const {url, headers, content} = uploadPacket;

    return gistApi.put(url, content, {headers: headers})
        .then(response => {
            return responseHelper.handleResponse(response)
        })
        .then(response => {
            return response;
        })
        .catch(error => {
            let res = {
                "status": 500,
                "errorMsg": t("Sorry, we're having technical issues. Please try again.")
            };
            if (error && error.response) {
                res.status = error.response.status;
            }
            return Promise.reject( res );
        })
}

/**
 * Accepts a link (presumably server-stored resource) and calls to delete it
 *
 * @param link the link to the resource to be deleted
 * @returns {Promise<never>}
 */
function removeUploadResource(link){
    let myHeader = requestHelper.authHeader();
    let endPoint = '/api/sources/upload?link='+link;

    return gistApi.delete(endPoint, {headers: myHeader})
        .then(response => {
            return responseHelper.handleResponse(response);
        })
        .then(response => {
            return response;
        })
        .catch(error => {
            let res = {
                "status": 500,
                "errorMsg": t("Sorry, we're having technical issues. Please try again.")
            };
            if (error && error.response) {
                res.status = error.response.status;
                switch (error.response.status) {
                    case 400:
                        res.errorMsg = t('There seems to be problem with the url of the uploaded resource.');
                        break;

                    case 401:
                        res.errorMsg = t('Sorry, you are unauthorized to delete the resource.');
                        break;

                    default:
                        res.errorMsg = t("Sorry, we're having technical issues. Please try again.");
                        break;
                }
            }
            return Promise.reject(res);
        })
}

/**
 * Prepares response for handling 402 errors by examining at error response body
 *
 * @param errorStatus   the http status for the response (error status code)
 * @param errorData     the data for the error
 */
function prepareResponseFor402Error(errorStatus, errorData) {
    let response = {
        "status": errorStatus
    };

    let redirectPath, isExternalRedirect = false;
    // parse 402 reason and set response accordingly
    if (errorData && errorData.reason === sourceConstants.SOURCE_FEATURE_FAILURE.INSUFFICIENT_ORDER_GIST) { // gist allowances issue
        response.errorMsg = t("Upgrade your subscription to order more Gists.");
        redirectPath = "/pricing";      // redirect to plan selection page
        isExternalRedirect = true;
    } else if (errorData && errorData.reason === sourceConstants.SOURCE_FEATURE_FAILURE.SUBSCRIPTION_UPGRADE_NEEDED) { // subscription upgrade is necessary
        response.errorMsg = t("Upgrade your subscription to use the features.");
        redirectPath = "/pricing";      // redirect to plan selection page
        isExternalRedirect = true;
    } else {
        response.errorMsg = t("There’s a billing issue with your account. Try updating your payment info.");
        redirectPath = "/payment";      // redirect to payment update page
    }

    // build redirect adn toast notification parameters
    response.redirectParams = {"redirectTo": redirectPath, "isExternal": isExternalRedirect}    // redirect path
    response.toastParams = {"toastType": notificationConstants.NOTIFICATION_TYPES.ERROR}        // error type

    return response;
}